import React, { useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { logJobSaved, useMarkAsFavouriteMutation, useMarkAsUnfavouriteMutation } from 'services';
import Loader from '../Loader';
import useTranslate from 'hooks/useTranslate';

interface FavoriteBtnProps {
	isFavourite: boolean;
	setIsFavourite: (isFavourite: boolean) => void;
	jobId: number;
	refetch: () => void;
	dataLength: number;
	currentPage: number;
	setCurrentPage: (currentPage: number) => void;
	handleClick: any;
	className: string;
}

const FavoriteBtn: React.FC<FavoriteBtnProps> = ({
	isFavourite,
	setIsFavourite,
	jobId,
	refetch = () => null,
	dataLength = 0,
	currentPage = 1,
	setCurrentPage = () => null,
	handleClick = () => null,
	className = 'filled-btn fav-btn rounded-pill lh-1',
}) => {
	const { translate } = useTranslate();
	const { mutateAsync: markAsFavourite, isPending: isPendingFav } = useMarkAsFavouriteMutation();
	const { mutateAsync: markAsUnfavourite, isPending: isPendingUnFav } =
		useMarkAsUnfavouriteMutation();
	const [tooltipText, setTooltipText] = useState(translate('Extras.saveJob'));
	const btnClass = !isFavourite
		? 'whishlist-btn bg-white rounded-circle p-0 btn btn-primary'
		: 'filled-btn fav-btn rounded-pill lh-1';

	const handleFavoriteBtnClick = (evt: any) => {
		evt.stopPropagation();
		logJobSaved(jobId);
		const isLoggedIn = handleClick();
		if (isLoggedIn) {
			if (isFavourite) {
				markAsUnfavourite(jobId)
					.then((res: any) => {
						if (res?.success) {
							setIsFavourite(!isFavourite);
							if (currentPage > 1 && dataLength <= 1) {
								setCurrentPage(currentPage - 1);
							} else {
								refetch();
							}
						}
					})
					.catch((error) => {
						console.error('Failed to unfavourite the job:', error);
						toast.error(translate('ToastMessage.failedToRemoveFromFavourites')); // Notify the user of the failure
					});
			} else {
				markAsFavourite({ jobId })
					.then((res: any) => {
						if (res?.success) {
							setIsFavourite(!isFavourite);
						}
					})
					.catch((error) => {
						console.error('Failed to favourite the job:', error);
						toast.error(translate('ToastMessage.failedToAddToFavourites')); // Notify the user of the failure
					});
			}
		}
	};

	const handleMouseEnter = () => {
		setTooltipText(isFavourite ? translate('Sidebar.savedJobs') : translate('Extras.saveJob'));
	};

	const handleMouseLeave = () => {
		setTimeout(() => {
			setTooltipText(translate('Extras.saveJob'));
		}, 500);
	};

	return (
		<>
			{isPendingFav || isPendingUnFav ? (
				<Loader />
			) : (
				<OverlayTrigger
					placement="bottom"
					delay={{ show: 250, hide: 400 }}
					overlay={<Tooltip id="button-tooltip-2">{tooltipText}</Tooltip>}
				>
					<Button
						className={btnClass}
						onClick={handleFavoriteBtnClick}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						aria-label={isFavourite ? 'Remove from favorites' : 'Add to favorites'}
					>
						<i className={`ri-star-${isFavourite ? 'fill' : 'line'}`}></i>
					</Button>
				</OverlayTrigger>
			)}
		</>
	);
};

export default FavoriteBtn;
